import React, { useState, useEffect, useRef } from 'react';
import BuildPoemstyles from './BuildPoem.module.css';
import ConfettiPopup from './ConfettiPopup';
const API_URL = process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000';

const BuildPoem = () => {
  const [showConfetti, setShowConfetti] = useState(false);
  const [isFiveWordLine, setIsFiveWordLine] = useState(true);
  const [poemUnits, setPoemUnits] = useState(Array(20).fill(''));
  const [submittedPoem, setSubmittedPoem] = useState('');
  const [assessmentResult, setAssessmentResult] = useState('');
  const [modelAssessment, setModelAssessment] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [placeholderText, setPlaceholderText] = useState('');
  const [showBadgePrompt, setShowBadgePrompt] = useState(false);
  const [currentLevel, setCurrentLevel] = useState('');
  const [submissions, setSubmissions] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [hasXiucaiBadge, setHasXiucaiBadge] = useState(false);
  const videoRef = useRef(null);
  const placeholderContent = 'Type here...';

  useEffect(() => {
    const token = localStorage.getItem('token');
    setIsLoggedIn(!!token);

    if (token) {
      fetchUserLevel(token);
    }
  }, []);

  useEffect(() => {
    if (!poemUnits[0]) {
      let index = 0;
      const interval = setInterval(() => {
        setPlaceholderText(placeholderContent.substring(0, index));
        index = (index + 1) % (placeholderContent.length + 1);
      }, 200);
      return () => clearInterval(interval);
    }
  }, [poemUnits]);

  useEffect(() => {
    if (showBadgePrompt && videoRef.current) {
      videoRef.current.play();
    }
  }, [showBadgePrompt]);

  const handleToggle = () => {
    setIsFiveWordLine(!isFiveWordLine);
    setPoemUnits(Array(!isFiveWordLine ? 20 : 28).fill(''));
  };

  const handleInputChange = (index, value) => {
    const newPoemUnits = [...poemUnits];
    newPoemUnits[index] = value;
    setPoemUnits(newPoemUnits);
  };

  const renderPoemInputs = () => {
    const lineUnits = isFiveWordLine ? 5 : 7;
    const totalLines = 4;

    const lineBreaks = [];
    for (let i = 0; i < totalLines; i++) {
      const units = [];
      for (let j = 0; j < lineUnits; j++) {
        units.push(
          <input
            key={i * lineUnits + j}
            type="text"
            value={poemUnits[i * lineUnits + j]}
            onChange={(e) => handleInputChange(i * lineUnits + j, e.target.value)}
            className={BuildPoemstyles.unitBox}
            placeholder={j === 0 && i === 0 ? placeholderText : ''}
            onFocus={() => j === 0 && i === 0 && setPlaceholderText('')}
          />
        );
        if (isFiveWordLine && j === 1) {
          units.push(<div key={`gap-${i}-${j}`} className={BuildPoemstyles.gap}></div>);
        } else if (!isFiveWordLine && (j === 1 || j === 3)) {
          units.push(<div key={`gap-${i}-${j}`} className={BuildPoemstyles.gap}></div>);
        }
      }
      lineBreaks.push(
        <div key={`line-${i}`} className={BuildPoemstyles.line}>
          {units}
        </div>
      );
    }
    return lineBreaks;
  };

  const formatPoem = () => {
    const lineUnits = isFiveWordLine ? 5 : 7;
    const totalLines = 4;
    let poem = '';

    for (let i = 0; i < totalLines; i++) {
      const line = poemUnits.slice(i * lineUnits, (i + 1) * lineUnits).join(' ');
      poem += line + '\n';
    }

    return poem.trim();
  };

  const handleSubmission = async (type) => {
    const poem = submittedPoem.trim() || formatPoem();
  
    setIsLoading(true);
    setErrorMessage('');
    setSuccessMessage('');
    try {
      const endpoint = type === 'manual' ? 'manual-poem-submission' : 'assess-poem';
      const payload = {
        poem: poem,
        user_level: currentLevel,
        level: 'Xiucai', // or 'Gongshi' depending on user selection or logic
      };
  
      const token = localStorage.getItem('token');
      const headers = {
        'Content-Type': 'application/json',
        ...(token && { 'Authorization': `Bearer ${token}` }),
      };
  
      const response = await fetch(`${API_URL}/${endpoint}`, {
        method: 'POST',
        headers: headers,
        body: JSON.stringify(payload),
      });
  
      const data = await response.json();
  
      if (response.ok && data.is_valid) {
        if (type === 'manual') {
          setSuccessMessage(data.message || 'Poem submitted for manual review.');
        } else {
          setAssessmentResult(data.model_assessment); // Display AI feedback
          if (isLoggedIn && !hasXiucaiBadge) {
            setShowBadgePrompt(true);
          }
        }
      } else {
        setErrorMessage(data.feedback || 'Failed to submit poem.');
        setAssessmentResult('');
      }
    } catch (error) {
      console.error('Error submitting poem:', error);
      setErrorMessage('Error submitting poem.');
    }
    setIsLoading(false);
  };
  

  const fetchUserLevel = async (token) => {
    try {
      const response = await fetch(`${API_URL}/user/current-level`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });
      const data = await response.json();
      setCurrentLevel(data.level);

      if (data.achievements && data.achievements.Xiucai) {
        setHasXiucaiBadge(true);
      }
    } catch (error) {
      console.error('Error fetching user level:', error);
    }
  };
  
  const handleBadgeSubmission = async (passed) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No token found. Please log in again.');
      }
  
      if (passed) {
        // Only make the API call if the user selects "Yes"
        const response = await fetch(`${API_URL}/user/achievements/Xiucai`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({ level: 'Xiucai' }),
        });
  
        if (response.ok) {
          setShowConfetti(true);
          setCurrentLevel('Xiucai');
          setHasXiucaiBadge(true);
          setTimeout(() => {
            closeConfetti();
          }, 10000);
        } else {
          console.error('Failed to update user level.');
        }
      } else {
        // If the user selects "No," display a try again message
        setErrorMessage('Try again and make your poem better based on the assessment feedback.');
      }
    } catch (error) {
      console.error('Error updating user level:', error);
    }
    // Hide the badge prompt regardless of the user’s choice
    setShowBadgePrompt(false);
  };
  
  const closeConfetti = () => {
    setShowConfetti(false); // Hide the confetti
    window.location.reload(); // Reload the page immediately
  };

  const openSubmissionsModal = () => {
    setIsModalOpen(true);
    const token = localStorage.getItem('token');
    if (token) {
      fetchSubmissions(token);
    }
  };

  const fetchSubmissions = async (token) => {
    try {
      const response = await fetch(`${API_URL}/submissions/?level=Xiucai`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });
      const data = await response.json();

      if (Array.isArray(data)) {
        setSubmissions(data);
      } else {
        setSubmissions([]);
      }
    } catch (error) {
      console.error('Error fetching submissions:', error);
      setSubmissions([]);
    }
  };

  const handleReadMore = (submission) => {
    setSelectedSubmission(submission);
  };

  const renderPoemWithLineBreaks = (poem) => {
    if (!poem) {
      return <span>No poem provided.</span>;
    }

    const lines = poem.split('\n');
    return lines.map((line, index) => (
      <React.Fragment key={index}>
        {line}
        {index !== lines.length - 1 && <br />}
      </React.Fragment>
    ));
  };

  const renderSubmissionsModal = () => (
    <div className={`${BuildPoemstyles.modal} ${isModalOpen ? BuildPoemstyles.modalOpen : ''}`}>
      <div className={BuildPoemstyles.modalContent}>
        <span className={BuildPoemstyles.closeButton} onClick={() => setIsModalOpen(false)}>&times;</span>
        <h3>Your Submissions</h3>
        {Array.isArray(submissions) && submissions.length === 0 ? (
          <p>No submissions yet.</p>
        ) : (
          <div className={BuildPoemstyles.submissionsContainer}>
            {Array.isArray(submissions) && submissions.map((submission, index) => (
              <div key={index} className={BuildPoemstyles.submissionItem}>
                <p><strong>Poem:</strong> {renderPoemWithLineBreaks(submission.poem)}</p>
                <p>
                  <strong>Feedback:</strong>
                  {submission.submission_type === 'manual' && submission.status === 'finished' ? (
                    submission.feedback || 'No feedback available'
                  ) : submission.submission_type === 'manual' && submission.status !== 'finished' ? (
                    <span className={BuildPoemstyles.pending}>Pending</span>
                  ) : submission.model_assessment.length > 100 ? (
                    <>
                      {submission.model_assessment.slice(0, 100)}...
                      <span
                        className={BuildPoemstyles.readMore}
                        onClick={() => handleReadMore(submission)}
                      >
                        Read More
                      </span>
                    </>
                  ) : (
                    submission.model_assessment || 'No feedback available'
                  )}
                </p>
                <p><strong>Type:</strong> {submission.submission_type}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  const renderReadMoreModal = () => (
    selectedSubmission && (
      <div className={`${BuildPoemstyles.modal} ${BuildPoemstyles.modalOpen}`} style={{ zIndex: 1100 }}>
        <div className={BuildPoemstyles.readMoreModalContent}>
          <span className={BuildPoemstyles.closeButton} onClick={() => setSelectedSubmission(null)}>&times;</span>
          <h3>Submission Details</h3>
          <p className={BuildPoemstyles.poem}><strong>Poem:</strong> {renderPoemWithLineBreaks(selectedSubmission.poem)}</p>
          <p className={BuildPoemstyles.feedback}><strong>Feedback:</strong> {selectedSubmission.submission_type === 'ai' ? selectedSubmission.model_assessment : selectedSubmission.feedback || 'No feedback available'}</p>
          <p className={BuildPoemstyles.type}><strong>Type:</strong> {selectedSubmission.submission_type}</p>
          <p className={BuildPoemstyles.status}><strong>Status:</strong> {selectedSubmission.status}</p>
          <p className={BuildPoemstyles.submittedAt}><strong>Submitted At:</strong> {new Date(selectedSubmission.submitted_at).toLocaleString()}</p>
        </div>
      </div>
    )
  );

  const renderManualAssessmentPrompt = () => (
    isLoggedIn && (
      <div className={BuildPoemstyles.manualAssessmentContainer}>
        <p>If you want a manual assessment for more accuracy or if you disagree with the model, you can make a manual submission and receive feedback after a few days or weeks by the admin (check your submissions to see the feedback).</p>
        <button
          onClick={() => handleSubmission('manual')}
          disabled={isLoading}
          className={BuildPoemstyles.manualButton}
          title="Your submission will be sent for manual review."
        >
          Submit for Manual Assessment
        </button>
      </div>
    )  
  );
  
  
  const renderBadgePrompt = () => (
    <div className={BuildPoemstyles.badgePrompt}>
      <h4>Did you pass the Xiucai level?</h4>
      <button className={BuildPoemstyles.button} onClick={() => handleBadgeSubmission(true)}>Yes</button>
      <button className={BuildPoemstyles.button} onClick={() => handleBadgeSubmission(false)}>No</button>
    </div>
  );

  return (
    <div className={BuildPoemstyles.container}>
      {showConfetti && (
        <ConfettiPopup
          message="Congratulations! You now have a Xiucai badge!"
          onClose={closeConfetti}
        />
      )}
      <button onClick={openSubmissionsModal} className={`${BuildPoemstyles.openModalButton} ${BuildPoemstyles.button}`}>
        View Previous Submissions
      </button>
      <h2>Test a Poem</h2>
      <div className={BuildPoemstyles.toggleContainer}>
        <span>{isFiveWordLine ? '5-word per line' : '7-word per line'}</span>
        <label className={BuildPoemstyles.switch}>
          <input type="checkbox" checked={!isFiveWordLine} onChange={handleToggle} />
          <span className={`${BuildPoemstyles.slider} ${BuildPoemstyles.round}`}></span>
        </label>
      </div>
      <p>Type in unit by unit:</p>
      {renderPoemInputs()}
      <p>OR</p>
      <p>Drop your whole poem</p>
      <textarea
        placeholder={"Enter your poem here...\n\nNote: Please remove all punctuation from your poem including apostrophes!"}
        value={submittedPoem}
        onChange={(e) => setSubmittedPoem(e.target.value)}
        className={BuildPoemstyles.textArea}
      />
      <div className={BuildPoemstyles.submitButtonsContainer}>
        <button
          onClick={() => handleSubmission('ai')}
          disabled={isLoading}
          className={`${BuildPoemstyles.aiButton} ${BuildPoemstyles.button}`}
          title="You will receive an instant AI assessment."
        >
          Submit for AI Assessment
        </button>
      </div>
      {isLoading && <div className={BuildPoemstyles.loader}></div>}
      {errorMessage && <div className={BuildPoemstyles.errorMessage}>{errorMessage}</div>}
      {successMessage && <div className={BuildPoemstyles.successMessage}>{successMessage}</div>}
      {assessmentResult && (
        <div className={BuildPoemstyles.assessmentResult}>
          {assessmentResult}
          {!hasXiucaiBadge && showBadgePrompt && renderBadgePrompt()}
          {renderManualAssessmentPrompt()}
        </div>
      )}
      {modelAssessment && (
        <div className={BuildPoemstyles.modelAssessment}>
          <h4>Model Assessment</h4>
          <p className={BuildPoemstyles.generatedText}>{modelAssessment}</p>
        </div>
      )}
      {renderSubmissionsModal()}
      {renderReadMoreModal()}
    </div>
  );
};

export default BuildPoem;

